import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import logger from '@gral/log';
import { createRoot } from 'react-dom/client';
import _ from 'lodash';
import ReactDOMServer from 'react-dom/server';

import Slider from '@mui/material/Slider';
import { Button, Chip, Modal, Typography } from '@ts/storybook';
//@ts-ignore
import MapWorker from '../utils/map.worker';

import OlMap from 'ol/Map';
import OlOverlay from 'ol/Overlay';
import OlLayerTile from 'ol/layer/Tile';
import OlSourceOSM from 'ol/source/OSM';
import OlFeature, { FeatureLike } from 'ol/Feature';
import OlStyle from 'ol/style/Style';
import OlPoint from 'ol/geom/Point';
import OlStroke from 'ol/style/Stroke';
import OlFill from 'ol/style/Fill';
import OlText from 'ol/style/Text.js';
import OlCircleStyle from 'ol/style/Circle.js';
import OlRegularShape from 'ol/style/RegularShape.js';
import OlLayerVector from 'ol/layer/Vector';
import OlCluster from 'ol/source/Cluster.js';
import OlSourceVector from 'ol/source/Vector';
import OlCollection from 'ol/Collection';
import OlMousePosition from 'ol/control/MousePosition';
import OlLineString from 'ol/geom/LineString';
import OlLayerImage from 'ol/layer/Image';
import OlSourceGeoJSON from 'ol/format/GeoJSON';
import OlSourceImageStatic from 'ol/source/ImageStatic';
import * as OlProj from 'ol/proj';
import envJson from '../../../../.env.json';

import { shiftKeyOnly } from 'ol/events/condition.js';
import { Attribution, defaults as defaultControls } from 'ol/control.js';
import { putUserDefaultView, getSettings, postUserDefaultView } from '@services/ApiService';
import { getBadge } from '@flux/stores/cxStore';
import { requestActions } from '@redux/slices/functionals/request/requestSlice';

import OrderedColor from '@components/Maps/Map/OrderedColor';
import MapIndoorLayer from './MapIndoorLayer';
import { Icon } from '@ts/storybook';
import TTooltipMap from '../TTooltipMap';
import Tooltip from '@components/Outputs/Tooltip';
import Loading from '@components/Loading';
import {
  isExtentIntersects,
  generateOpenLayerMapView,
  generateIntermediedPointAndDirectionalMarkersWorm,
  getIndexLayers,
  paintGeometryLayer,
  computeExtent,
  generateLinesWorm,
  createLocJsonObject,
} from '../utils/map.utils';

import App from '@gral/app';
import { useSetState } from 'react-use';
import { store, useAppDispatch, useAppSelector, useObjectSelector } from '@redux/store';
import BaseLayer from 'ol/layer/Base';
import useEffectAfterMount from '@utils/hooks/useEffectAfterMount';
import { Provider } from 'react-redux';
import Geometry from 'ol/geom/Geometry';
import { CLocation, CReading, TLayerID, TElementID, formatValue, EAclAction } from '@ts/ui-models';
import { IFilterState } from '@modules/newMap/WrapperMapView';
import { Coordinate } from 'ol/coordinate';
import useEffectDebounced from '@utils/hooks/useEffectDebounced';
import { useMapContext } from '@pages/MapPage';
import { calculateThreshold } from '../utils/map.worker.utils';
import { CMapLayer } from '@models/maps/Layer';
import { TRecordItems, TRecordLabels, TRecordLocation } from '@interfaces/maps/Map.interface';
import { CMapElement, TUpdateMap } from '@models/maps/Map';
import XYZ from 'ol/source/XYZ';
import { alpha, useTheme } from '@mui/material';
import { useMqttIsConnected } from '@components/Utils/_hooks/useMqttIsSelected';
import { setVisibilityIconDebounced } from '../utils/iconVisibility.utils';

require('./styles.css');

// Hover positions
const BOTTOM_RIGHT = 'bottom-right';
const BOTTOM_LEFT = 'bottom-left';
const TOP_RIGHT = 'top-right';
const TOP_LEFT = 'top-left';

const accessToken = envJson.APIKEY_MAP;
const xyzSource = new XYZ({
  //url: `${basemapURL}/${serviceName}/MapServer/tile/{z}/{y}/{x}?token=${accessToken}`,
  url: `https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token=${accessToken}`,
});

const TILE_MAPS_LAYERS = {
  osmLayer: new OlLayerTile({
    source: new OlSourceOSM(),
  }),
  satelliteLayer: new OlLayerTile({
    source: xyzSource,
  }),
};

export type TMapHandle = {
  onCenterView: () => void;
  getOlmap: () => OlMap | null;
  cleanFeaturesMap: () => void;
  onChangeGlobalClusterVisibility: (visibility: boolean) => void;
};
export interface IMapNeeds {
  layers: CMapLayer[];
  items: TRecordItems;
  labels: TRecordLabels;
  //locations: CLocation[] | null;
  locations: TRecordLocation | null;
}

interface MapProps {
  mapNeeds: IMapNeeds;
  elementsMapSelected: (CMapElement | CLocation)[];
  elementSelected: CMapElement | CLocation | null;

  filtersState: IFilterState;

  //inheritedMapProps;

  onClickItem: (itemId: string | null) => void;
  onClickCluster: (items: (CMapElement | CLocation)[]) => void;
}

var _popupOverlay: any = null;
const log = logger(`MAP`);

const TOlMap = forwardRef<TMapHandle | undefined, MapProps>((props, ref) => {
  //console.log('Map', props);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(state => state.global.settings);
  const { loading }: { loading: boolean } = useObjectSelector(state => state.request['mapPositions']);
  const [mapLoading, setMapLoading] = useState(true);
  const isConnected = useMqttIsConnected();

  const { layers, items, locations } = props.mapNeeds;
  const mapContext = useMapContext();
  if (!mapContext) return;
  const { view, cycleState, setCycleState, mapObserver } = mapContext;
  const { filtersState, elementsMapSelected, elementSelected } = props;
  const filtersStateRef = useRef(filtersState);

  //#region Variables Map
  const mapRef = useRef<HTMLDivElement>(null);
  const [olmap, setOlMap] = useState<OlMap | null>(null);
  const [mapIndoorLayer, setMapIndoorLayer] = useState<MapIndoorLayer | null>(null);
  const tooltipRootRef = useRef<any>(null);

  const mainFeaturesRef = useRef<OlSourceVector>(
    new OlSourceVector({
      features: new OlCollection<any>(),
    })
  );

  const mainFeatures = mainFeaturesRef.current; //All features before filtering
  const layersGeometriesRef = useRef<Record<TLayerID, OlSourceVector>>({});
  const layersGeometries = layersGeometriesRef.current;
  const idClusterLayer = 'globalClusterLayer';
  const globalClusterLayerRef = useRef<OlLayerVector<OlCluster<any>, OlFeature<Geometry>> | null>(null);
  const globalClusterLayer = globalClusterLayerRef.current;
  const clusterSourceRef = useRef<OlCluster<any>>(null);
  const clusterSource = clusterSourceRef.current;
  const idItemSelectedLayer = 'itemsSelectedLayer';
  const itemsSelectedLayerRef = useRef<OlLayerVector<OlSourceVector, OlFeature<Geometry>> | null>(null);
  const itemsSelectedLayer = itemsSelectedLayerRef.current;
  //#endregion

  const tooltip = document.getElementById('tooltip');

  const [state, setState] = useSetState({
    isMapOutdoor: view.viewTypeId === 'map2',
    isMapIndoor: view.viewTypeId === '2d',
    isSatelliteTile: Boolean(settings?.defaultView?.tile === 'satellite'),
    canEditMapIndoor: false,
    isEditingMapIndoor: false,
    isOpenConfig: false,
    isOpenDialogEditMapIndoor: false,
    transparency: settings?.defaultView?.transparency ?? 0.5,
    checkPermision: App.cxStore.checkPermission('taView', EAclAction.EDIT),
    selectedItemID: null,
  });

  //#region Variables Color
  const orderedColorRef = useRef<OrderedColor | null>(null);

  if (!orderedColorRef.current) {
    orderedColorRef.current = new OrderedColor();
  }

  const orderedColor = orderedColorRef.current;
  //#endregion

  const isGPSMap = view.isWorldView();
  const readingTypeId = view.getReadingTypePosition();
  const projectionCode = view.getProjectionCode();

  const { view: mapView, projection } = generateOpenLayerMapView(view);

  //#region HandleChanges
  const moveStartOnMap = () => {
    //movingMap = true;
    //olmap?.un('pointermove', throttledCursorStyling);
    //olmap?.un('pointermove', throttledDisplayTooltip);
    olmap?.on('pointermove', throttledAdjustZoomIndoorLayer);
  };
  const moveEndOnMap = () => {
    //movingMap = false;
    //const center = olmap?.getView().getCenter();
    //const zoom = olmap?.getView().getZoom();

    //olmap?.on('pointermove', throttledCursorStyling);
    //olmap?.on('pointermove', throttledDisplayTooltip);
    olmap?.un('pointermove', throttledAdjustZoomIndoorLayer);
  };

  const onRecenterMap = (firstCentringView?: boolean) => {
    if (elementsMapSelected.length > 0) {
      centerViewOnLayer(idItemSelectedLayer);
    } else centerViewOnLayer(firstCentringView ? 'geometriesLayer' : undefined);
  };

  const onChangeTileView = () => {
    const layers = olmap?.getLayers() ?? [];

    layers.forEach(layer => {
      if (layer instanceof OlLayerTile) {
        olmap?.removeLayer(layer);
      }
    });

    if (state.isSatelliteTile) {
      setState({ ...state, isSatelliteTile: false });
      olmap?.addLayer(TILE_MAPS_LAYERS.osmLayer);
    } else {
      setState({ ...state, isSatelliteTile: true });
      olmap?.addLayer(TILE_MAPS_LAYERS.satelliteLayer);
    }
  };

  const onClickEditConfiguration = () => {
    setState({
      ...state,
      isOpenConfig: !state.isOpenConfig,
    });
  };

  const onClickEditMapIndoorLayer = () => {
    if (!state.isEditingMapIndoor && mapIndoorLayer) {
      setState({
        ...state,
        isEditingMapIndoor: !state.isEditingMapIndoor,
      });
      mapIndoorLayer.isEditMapIndoor = !state.isEditingMapIndoor;
    } else {
      setState({
        ...state,
        isOpenDialogEditMapIndoor: true,
      });
    }
  };

  const onSubmitSaveMapIndoor = () => {
    if (mapIndoorLayer) {
      setState({
        ...state,
        isOpenDialogEditMapIndoor: false,
        isOpenConfig: false,
        isEditingMapIndoor: !state.isEditingMapIndoor,
      });
      mapIndoorLayer.isEditMapIndoor = !state.isEditingMapIndoor;
    }
  };

  const onClickMap = async event => {
    const clickedFeatures = await globalClusterLayer?.getFeatures(event.pixel);

    if (clickedFeatures && clickedFeatures.length > 0) {
      const clickedFeature = clickedFeatures[0];

      const clusterFeatures = clickedFeature.get('features');

      if (clusterFeatures) {
        log.info('OnClick Cluster:', clusterFeatures);
        props.onClickCluster(clusterFeatures.map(r => items[r.get('itemID')]));
      } else log.info('OnClick Feature:', clickedFeature);
    } else {
      const clickedItem = await itemsSelectedLayer?.getFeatures(event.pixel);
      if (clickedItem && clickedItem.length > 0) {
        log.info('OnClick Feature:', clickedItem);
        props.onClickItem(clickedItem?.[0]?.get('itemID') ?? null);
      } else {
        const clickedLocations = await olmap?.getFeaturesAtPixel(event.pixel);
        if (clickedLocations && locations) {
          log.info('Click on location');
          const listIDs = clickedLocations.map(l => locations[l.get('entityID')]);
          if (listIDs.length > 0) props.onClickCluster(listIDs);
          else {
            log.info('Click on nothing');
            //props.onClickItem(null);
          }
        }
      }
    }
  };

  //#endregion

  //#region Function Map
  /**
   * Handles post-rendering operations to print the background for indoor layers.
   * This function ensures that only one indoor map layer is loaded and updates the state accordingly.
   */
  const afterRenderMapPrintBackground = () => {
    // Flag to ensure only one indoor map layer is processed
    let onlyOneMapIndoor = false;

    // Iterate over each layer to check for background properties
    layers.forEach(layer => {
      const { background } = layer;

      // If a background exists and no indoor layer has been processed yet
      if (background && !onlyOneMapIndoor) {
        const locator = background.locator;

        // If the map is currently set as outdoor, initialize a new indoor map layer
        if (state.isMapOutdoor) {
          setMapIndoorLayer(new MapIndoorLayer(olmap, locator, layer, layers, state.transparency));
        }

        // Update state to allow editing of the indoor map layer
        setState({ ...state, canEditMapIndoor: true });

        // Set flag to true to prevent further processing of additional indoor layers
        onlyOneMapIndoor = true;
      }
    });
  };

  const displayTooltip = async evt => {
    if (!olmap) return;
    if (shiftKeyOnly(evt)) return;

    const { frameState, pixel } = evt;
    const { size } = frameState;
    let clusterFeatures: FeatureLike[] = [];
    let listFeatures: FeatureLike[] = [];

    if (globalClusterLayer?.getVisible()) {
      // Get features from cluster or map (locations)
      clusterFeatures = await globalClusterLayer.getFeatures(evt.pixel);
      if (clusterFeatures.length > 0) {
        listFeatures = clusterFeatures[0].get('features');
      }
      //Locations
      if (listFeatures.length === 0) {
        listFeatures = olmap?.getFeaturesAtPixel(evt.pixel);
      }
    } else {
      //Item selected layer
      listFeatures = olmap?.getFeaturesAtPixel(evt.pixel);
    }

    if (!tooltip) return;

    if (listFeatures.length === 0) {
      tooltip.style.display = 'none';
      return;
    } else tooltip.style.display = '';

    // Create root tooltipRef
    if (!tooltipRootRef.current) {
      tooltipRootRef.current = createRoot(tooltip); // Crear el root una vez
    }

    //Update tooltip (revisar no necesitamos update siempre solo al cambio de features)
    tooltipRootRef.current.render(renderTooltip(listFeatures));

    //#region Positions tooltip
    let overlayPos = getOverlayTooltipPosition(pixel, size) || 'top-center';

    const overlayContainer = _popupOverlay?.getElement().parentNode;
    if (!overlayContainer) return;

    if (overlayPos.split('-')[0] === 'top') {
      overlayContainer.classList.add('ol-top');
      overlayContainer.classList.remove('ol-bottom');
    }
    if (overlayPos.split('-')[0] === 'bottom') {
      overlayContainer.classList.add('ol-bottom');
      overlayContainer.classList.remove('ol-top');
    }

    _popupOverlay.setPosition(evt.coordinate);
    _popupOverlay.setPositioning(overlayPos);
    //#endregion
  };

  const callbackObserver = (tUpdateMap: TUpdateMap) => {
    const { type, updatedItem: item, data } = tUpdateMap;
    switch (type) {
      case 'updateReadings':
        //log.info(`Move ${item.getName()} Features On Map`);
        generateFeaturesFromReadingsPositions(item);
        item.processNewReadings();
        break;
      case 'removeReadings':
        //log.info(`Remove ${item.getName()} lines and point On Map`);
        removeFeaturesFromReadingsId(data);
        break;

      case 'remarkFeature':
        //log.info(`Remark ${item.getName()} Features On Map`);
        updateIconPositionAndIcon(item);
        break;

      case 'updateFilters':
        setCycleState(prevState => ({ ...prevState, reSyncData: prevState.reSyncData + 1 }));
        break;

      default:
        break;
    }
  };

  //Adjusts the zoom depending on whether we are in an indoor layer
  const adjustZoomIndoorLayer = () => {
    if (!olmap) return;
    if (mapIndoorLayer) {
      const extentImageLayer = mapIndoorLayer.getExtent;
      const viewExtent = olmap?.getView().calculateExtent(olmap?.getSize());

      const view = olmap?.getView();
      if (extentImageLayer && isExtentIntersects(viewExtent, extentImageLayer)) {
        view.setMaxZoom(24);
      } else {
        view.setMaxZoom(20);
      }
    }
  };

  //#region Clusters
  const createGlobalClusterLayer = () => {
    const mapLayerAlreadyCreated = checkIfLayerCreated(idClusterLayer);
    if (!mapLayerAlreadyCreated) {
      clusterSourceRef.current = new OlCluster({
        distance: 40,
        source: new OlSourceVector<any>({
          features: [],
        }),
        // geometryFunction: geometryFunctionCluster,
      });

      const clusterLayer = new OlLayerVector({
        source: clusterSourceRef.current,
        //@ts-ignore
        style: (features: OlFeature) => {
          if (!clusterLayer.getVisible()) return null;
          const size = features.get('features').length;

          //If the cluster have only one feature
          if (size === 1) {
            const feature: OlFeature<Geometry> = features.get('features')[0];
            const itemID = feature?.get('itemID');
            setVisibilityIconDebounced(itemID, null, true);
            return feature.getStyle();
          }

          //If the cluster have more than one feature
          const feats = features.get('features');
          feats.forEach(f => setVisibilityIconDebounced(f.get('itemID'), null, false));

          features.set('isCluster', true);

          return [
            new OlStyle({
              image: outerCircle(size),
            }),
            new OlStyle({
              image: innerCircle(size),
              text: clusterText(size),
            }),
          ];
        },
      });
      clusterLayer.set('id', idClusterLayer);
      clusterLayer.set('name', idClusterLayer);
      clusterLayer.setZIndex(getIndexLayers('cluster'));
      olmap?.addLayer(clusterLayer);

      globalClusterLayerRef.current = clusterLayer;
    }
  };

  const updateClusterFeatures = (filtersState: IFilterState) => {
    log.info('Update clusters', filtersState);

    const selectedItems = filtersState.items.selected;

    const visibleFeatures =
      mainFeatures?.getFeatures().filter(feature => {
        const itemId = feature.get('itemID');
        const itemVisible = selectedItems.has(itemId);
        if (!itemVisible) setVisibilityIconDebounced(itemId, null, false);
        return itemVisible;
      }) ?? [];

    clusterSource?.getSource()?.clear();
    clusterSource?.getSource()?.addFeatures(visibleFeatures);
  };
  //#endregion

  //#region Views
  const centerViewOnLayer = (layerID?: TLayerID) => {
    log.info('CenterViewOnLayer', layerID ?? 'GlobalClusterLayer');
    let vectorSource;
    if (layerID === 'geometriesLayer' && state.isMapOutdoor) vectorSource = getGlobalVectorGeometries();
    else if (layerID === idItemSelectedLayer) vectorSource = itemsSelectedLayer?.getSource();
    else if (layerID) vectorSource = mainFeatures;
    else vectorSource = clusterSource?.getSource();

    if (vectorSource) {
      const extent: any[] = vectorSource.getExtent();
      const isValidExtent = !extent.every(value => isNaN(value) || value === Infinity || value === -Infinity);
      if (extent && isValidExtent) {
        setTimeout(() => {
          olmap?.getView().fit(extent, {
            size: olmap?.getSize(),
            padding: [50, 50, 50, 50],
            //duration: 500, // Animación (opcional)
            maxZoom: 19,
          });
        }, 200);
      } else {
        setTimeout(() => {
          if (state.isMapIndoor && view.size && view.size[0] && view.size[1]) {
            olmap?.getView().fit([0, 0, view.size[0], view.size[1]], {
              size: olmap?.getSize(),
              padding: [50, 50, 50, 50],
              maxZoom: 19,
            });
          }
        }, 200);
      }
    }
  };

  const centerViewOnFeature = (itemID: string) => {
    const feature = items[itemID].mainFeature;
    if (feature) {
      const geometry = feature.getGeometry();
      const extent = geometry?.getExtent();

      if (extent) {
        olmap?.getView().fit(extent, {
          duration: 1000, // Duración de la animación
          //maxZoom: 16, // Zoom máximo permitido
          padding: [50, 50, 50, 50], // Espacio entre la geometría y los bordes de la vista
        });
      }
    }
  };
  //#endregion

  //#region Layers
  const getGlobalVectorGeometries = () => {
    return new OlSourceVector<any>({
      features: Object.values(layersGeometries).flatMap(source => source.getFeatures()),
    });
  };

  /**
   * Filters and updates the visibility or transparency of map layers based on the provided filter state.
   *
   * @param {IFilterState} filterState - The current filter state containing selected and non-selected layers.
   */
  const filtersLayers = (filterState: IFilterState) => {
    olmap?.getLayers().forEach((olLayer: BaseLayer) => {
      const olLayerID = olLayer.get('id'); // Unique ID for layers
      const olLayerName = olLayer.get('name'); // Name of the layer, can be duplicated

      // Skip tile layers
      if (olLayer instanceof OlLayerTile) return;

      // Skip cluster layer
      if (olLayerID === idClusterLayer) return;

      // Handle image layers with indoor map logic
      if (olLayer instanceof OlLayerImage && olLayerID?.startsWith('imageLayer') && mapIndoorLayer) {
        // Adjust transparency to hide or show the image layer
        if (!filterState.layers.selected.has(olLayerName)) {
          if (mapIndoorLayer.transparency !== 0) mapIndoorLayer.transparency = 0;
        } else if (mapIndoorLayer.transparency !== state?.transparency) mapIndoorLayer.transparency = state?.transparency ?? 0.5;

        return;
      }

      // Set visibility for other geometry and item layers
      olLayer.setVisible(filterState.layers.selected.has(olLayerName));
    });
  };

  /**
   * Initializes the map layers based on the provided array of `layers` and `readings`.
   *
   * @param {*} layers - An array of layer objects to initialize on the map.
   */
  const initializeMapLayers = (layers: CMapLayer[]) => {
    const itemSelectedVectorLayer = new OlLayerVector({
      source: new OlSourceVector({
        features: new OlCollection<any>(),
      }),
      zIndex: getIndexLayers('items'),
    });
    itemSelectedVectorLayer.set('id', idItemSelectedLayer);
    itemSelectedVectorLayer.set('name', idItemSelectedLayer);
    olmap?.addLayer(itemSelectedVectorLayer);
    itemsSelectedLayerRef.current = itemSelectedVectorLayer;

    layers.forEach(taLayer => {
      checkAndCreateSourceVector(taLayer);

      if (state.isMapIndoor) generateMapIndoorLayerImages(taLayer);
      addVectorLayerToOlmap(taLayer);
    });
  };

  /**
   * Adds a new layer to the map based on the provided `taLayer` object.
   *
   * @param {*} taLayer - The layer object to be added to the map.
   */
  const addVectorLayerToOlmap = (taLayer: CMapLayer) => {
    const layerID = taLayer.getID();
    const mapLayerAlreadyCreated = checkIfLayerCreated(layerID);

    if (!mapLayerAlreadyCreated) {
      const vectorSourceGeo = layersGeometries[layerID];
      if (taLayer.geometriesIds.length > 0) {
        //GEOMETRIES
        const vectorLayerGeo = new OlLayerVector({
          source: vectorSourceGeo,
          zIndex: getIndexLayers('locations'),
          style: (feature, resolution) => {
            const zoom = olmap?.getView().getZoomForResolution(resolution);
            return paintGeometryLayer(feature, 1, zoom);
          },
        });
        vectorLayerGeo.set('id', 'locations' + layerID);
        vectorLayerGeo.set('name', layerID);
        //vectorLayerGeo.set('taLayer', taLayer);

        olmap?.addLayer(vectorLayerGeo);
      }
      /*  if (taLayer.itemsIds.length > 0) {
        //ITEMS
        const vectorLayer = new OlLayerVector({
          source: vectorSource,
          zIndex: getIndexLayers('items'),
        });
        vectorLayer.set('id', 'items' + layerID);
        vectorLayer.set('name', layerID);
        //vectorLayer.set('taLayer', taLayer);

        olmap?.addLayer(vectorLayer);
      } */
    }
  };

  /**
   * Checks if the necessary objects and layers exist for the provided `taLayer`.
   * If they do not exist, creates the required objects and initializes them.
   *
   * @param {*} taLayer - The layer object to be checked and created if necessary.
   */
  const checkAndCreateSourceVector = (taLayer: CMapLayer) => {
    const layerID = taLayer.getID();
    /* if (!mainFeatures[layerID] && taLayer.itemsIds.length > 0) {
      mainFeatures[layerID] = new OlSourceVector({
        features: new OlCollection<any>(),
      });
      mainFeatures[layerID].set('taLayer', taLayer);
    } */
    if (!layersGeometries[layerID] && taLayer.geometriesIds.length > 0) {
      layersGeometries[layerID] = new OlSourceVector({
        features: new OlCollection<any>(),
      });
      layersGeometries[layerID].set('taLayer', taLayer);
    }
  };

  /**
   * Checks if a layer with the specified `itemID` has already been created on the map.
   *
   * @param {*} itemID - The logical ID of the layer to check.
   * @returns {boolean} - Returns `true` if a layer with the specified `itemID` is found, otherwise returns `false`.
   */
  const checkIfLayerCreated = (layerID: string) => {
    const olLayers = olmap?.getLayers() ?? [];
    const createdLayers: BaseLayer[] = [];
    olLayers.forEach(layer => {
      if (layer && layer.get('id') !== 'OlLayerTile') createdLayers.push(layer);
    });
    return createdLayers.find(layer => layer.get('id') === layerID);
  };

  /**
   * This function generates a background image layer for the map based on the provided taLayer indoor.
   * @param {*} taLayer
   */
  const generateMapIndoorLayerImages = (taLayer: CMapLayer) => {
    const { background } = taLayer;
    const layerID = taLayer.getID();
    const backgroundLayerCreated = checkIfLayerCreated(layerID);

    if (background && !backgroundLayerCreated) {
      const imageExtent = computeExtent(taLayer.properties);
      if (imageExtent.length) {
        const locator = background.locator;

        const backgroundImage = new OlSourceImageStatic({
          url: locator,
          projection: projection.getCode(),
          imageExtent,
        });

        const backgroundLayer = new OlLayerImage({
          source: backgroundImage,
        });
        backgroundLayer.set('id', 'indoor' + layerID);
        backgroundLayer.set('name', layerID);
        olmap?.addLayer(backgroundLayer);
      }
    }
  };

  /**
   * Generates map layer locations based on the given TA layer.
   *
   * @param {*} taLayer - the TA layer to generate locations for
   */
  const generateMapLayerGeometries = (taLayer: CMapLayer) => {
    const sourceGeo = layersGeometries[taLayer.getID()];
    if (!sourceGeo || !locations) return;

    for (let location of Object.values(locations)) {
      const geoID = location.getID();
      const geoName = location.getName();

      if (!taLayer.geometriesIds.includes(geoID)) continue;

      const existingFeature = sourceGeo.getFeatureById(geoID);

      if (!existingFeature) {
        const geojsonObject = createLocJsonObject([location], projectionCode);
        const geometriesFeatures = new OlSourceGeoJSON({
          geometryName: geoName,
        }).readFeatures(geojsonObject, {
          dataProjection: projectionCode,
          featureProjection: 'EPSG:3857',
        });

        geometriesFeatures.forEach(feature => {
          feature.setId(geoID);
          feature.set('tLocation', location);
        });

        sourceGeo.addFeatures(geometriesFeatures);
      }
    }
  };
  //#endregion

  //#region Features
  const isValidCoordinate = (coord: number[]) => {
    const [x, y] = coord;
    return (
      x !== null &&
      y !== null &&
      x !== undefined &&
      y !== undefined &&
      !isNaN(x) &&
      !isNaN(y) &&
      x !== Infinity &&
      y !== Infinity &&
      x !== 0 &&
      y !== 0
    );
  };
  const generateWormFeatures = (
    item: CMapElement,
    transformedReadings: CReading[]
  ): { mainFeature: OlFeature<OlPoint>; lines: OlFeature<OlLineString>[]; markers: OlFeature<OlPoint>[] } => {
    const itemID = item.getID();
    //Create line Worm
    const customLines: OlFeature<OlLineString>[] = generateLinesWorm(
      itemID,
      transformedReadings,
      item.colorInMap,
      item.worm.lines.length
    );

    // Create feature markers
    const itemReadingsMarkers = transformedReadings.map((reading, idx) => {
      const feature = new OlFeature({
        isLast: idx === 0,
        geometry: reading.coords && new OlPoint(reading.coords),
        itemID,
        reading,
        //visible: true,
      });
      /* const onChangeVisibility = (e) => {
        setVisibilityIconDebouncedd(item.getID(), null, e.target.get('visible'));
      };

      if (idx === 0) {
        feature.on('change', onChangeVisibility);
      } else {
        feature.un('change', onChangeVisibility);
      } */
      feature.setId(`${itemID}`);

      return feature;
    });

    //Generate intermediedpoints
    const customMarkers: OlFeature<OlPoint>[] = generateIntermediedPointAndDirectionalMarkersWorm(
      itemID,
      item.colorInMap,
      itemReadingsMarkers,
      item.worm.markers.length
    );
    const mainFeature = itemReadingsMarkers[0];
    mainFeature?.setStyle(backgroundMarker());
    return {
      mainFeature,
      lines: [...customLines, ...item.worm.lines],
      markers: [...customMarkers, ...item.worm.markers],
    };
  };
  /**
   * Removes a feature or features from an OpenLayers vector source if it/they exist.
   * @param {string | string[]} itemID - the ID(s) of the feature(s) to remove
   * @param {OlSourceVector<any> | null} sourceVector - the vector source to search for the feature(s) in
   */
  const removeFeatureInSource = (itemID: string | string[], sourceVector?: OlSourceVector<any> | null) => {
    if (Array.isArray(itemID)) {
      const listFeaturesToRemove: any[] = [];
      itemID.forEach(id => {
        const feature = sourceVector?.getFeatureById(id);
        if (feature) {
          listFeaturesToRemove.push(feature);
        }
      });
      sourceVector?.removeFeatures(listFeaturesToRemove);
    } else {
      const existingFeature = sourceVector?.getFeatureById(itemID);
      if (existingFeature) {
        sourceVector?.removeFeature(existingFeature);
      }
    }
  };
  /**
   * Generates OlFeature objects for readings and returns them as an object containing itemReadingsLine and itemReadingsMarkers.
   *
   * @param {type} taLayer - the TA layer
   * @param {type} readings - the readings
   * @param {type} itemID - the logical ID of the item
   * @return {Object} an object containing itemReadingsLine and itemReadingsMarkers
   */
  const generateFeaturesFromReadingsPositions = (item: CMapElement) => {
    const vectorSourceCluster = clusterSource?.getSource();
    const vectorSourceItemSelecteds = itemsSelectedLayer?.getSource();
    const newReadings: CReading[] = item.newReadings?.[readingTypeId] ?? [];
    const itemID = item.getID();

    //GetLast Reading/MainFeature
    const featureLastReading: CReading | undefined = item.mainFeature?.get('reading');

    // Si existe una última lectura previa, agrégala al array de lecturas para poder hacer la linea
    const readingsToTransform: CReading[] = featureLastReading ? [...newReadings, featureLastReading] : newReadings ?? [];

    // Mapea y transforma las coordenadas, solo si es necesario (si es un mapa GPS)
    const transformedReadings = readingsToTransform
      .map(readingData => {
        const dataCoords: { x: number; y: number } = readingData.realData.data;
        if (dataCoords && typeof dataCoords === 'object' && 'x' in dataCoords && 'y' in dataCoords) {
          const { x, y }: { x: number; y: number } = dataCoords;

          const coords: Coordinate = isGPSMap ? OlProj.transform([x, y], 'EPSG:4326', 'EPSG:3857') : [x, y];

          readingData.coords = coords;
        }

        return readingData;
      })
      .filter(read => read.coords && isValidCoordinate(read.coords));

    // Update last point style
    const { mainFeature, lines, markers } = generateWormFeatures(item, transformedReadings);
    item.worm = { markers, lines };

    if (mainFeature) {
      removeFeatureInSource(itemID, mainFeatures); //Need to refresh feature
      item.mainFeature = mainFeature;
      updateIconPositionAndIcon(item);
      mainFeatures?.addFeature(mainFeature);
      removeFeatureInSource(itemID, vectorSourceCluster);
      vectorSourceCluster?.addFeature(mainFeature);

      if (!filtersStateRef.current.items.selected.has(itemID)) {
        removeFeatureInSource(itemID, vectorSourceCluster);
        deleteIcon(itemID);
      } else if (vectorSourceItemSelecteds?.getFeatureById(itemID)) {
        removeFeatureInSource(itemID, vectorSourceItemSelecteds);
        vectorSourceItemSelecteds?.getFeatures().forEach(feature => {
          const featureId = feature.getId()?.toString();
          if (featureId && featureId.toString().includes(item.getID())) {
            removeFeatureInSource(featureId, itemsSelectedLayer?.getSource());
          }
        });
        vectorSourceCluster?.addFeature(mainFeature);
        vectorSourceItemSelecteds.addFeatures([mainFeature, ...item.worm.lines, ...item.worm.markers]);
      } else if (itemsSelectedLayer?.get('newItem') === itemID) {
        //New item
        setVisibilityIconDebounced(itemID, null, true);
        vectorSourceCluster?.addFeature(mainFeature);
        itemsSelectedLayer?.set('newItem', '');
        vectorSourceItemSelecteds?.addFeatures([mainFeature, ...item.worm.lines, ...item.worm.markers]);
        centerViewOnLayer(idItemSelectedLayer);
      }
    } else {
      removeFeatureInSource(itemID, mainFeatures);
      removeFeatureInSource(itemID, vectorSourceCluster);
      removeFeatureInSource(itemID, vectorSourceItemSelecteds);
      deleteIcon(itemID);
    }
  };

  const removeFeaturesFromReadingsId = (readingsToRemove: string[]) => {
    const vectorSourceItemSelecteds = itemsSelectedLayer?.getSource();
    removeFeatureInSource(readingsToRemove, vectorSourceItemSelecteds);
  };

  /**
   * Filters out duplicate features from a list of features.
   *
   * @param {Array} listFeatures - The list of features to filter.
   * @return {Array} The filtered list of unique features.
   */
  const filterUniqueFeatures = (listFeatures: FeatureLike[]): OlFeature[] => {
    const uniqueValues = new Set();
    const filteredList: any[] = [];

    listFeatures.forEach(feature => {
      let elementID = feature?.get('itemID') ?? feature?.get('entityID');
      if (!elementID) {
        const features = feature.get('features');
        if (features) {
          feature = features[0];
          elementID = feature?.get('itemID') ?? feature?.get('entityID');
        }
      }

      if (!uniqueValues.has(elementID) && !feature.get('noTooltip')) {
        uniqueValues.add(elementID);
        filteredList.push(feature);
      }
    });

    return filteredList;
  };
  //#endregion

  //#endregion

  //#region Functions Tooltip & cursor
  /**
   * Determines the position of the overlay tooltip based on the provided pixel and size.
   *
   * @param {*} pixel - The pixel coordinates of the tooltip.
   * @param {*} size - The size of the tooltip.
   * @returns {*} The position of the overlay tooltip.
   */
  const getOverlayTooltipPosition = (pixel, size) => {
    let overlayPos: any = null;
    if (size[0] / 2 > pixel[0] && size[1] / 2 > pixel[1]) {
      // right, bottom size > top left
      overlayPos = TOP_LEFT;
    } else if (size[0] / 2 < pixel[0] && size[1] / 2 < pixel[1]) overlayPos = BOTTOM_RIGHT;
    else if (size[0] / 2 > pixel[0] && size[1] / 2 < pixel[1]) overlayPos = BOTTOM_LEFT;
    else if (size[0] / 2 < pixel[0] && size[1] / 2 > pixel[1]) overlayPos = TOP_RIGHT;

    return overlayPos;
  };
  /**
   * Generates an overlay tooltip for the map and then modifying its content.
   */
  const generateOverlayTooltip = () => {
    const tooltip = document.getElementById('tooltip');
    _popupOverlay = new OlOverlay({
      // @ts-ignore
      element: tooltip,
      id: 'popup',
      stopEvent: true,
    });

    olmap?.addOverlay(_popupOverlay);
  };
  /**
   * Sets the cursor styling based on the event.
   *
   * @param {*} evt - The event object.
   */
  const cursorStyling = async evt => {
    if (!olmap) return;

    const mapElement = olmap.getTargetElement();
    let cursorChanged = false;

    // Verificar clusters si existe la capa de clusters
    if (globalClusterLayer) {
      const clusterFeatures = await globalClusterLayer.getFeatures(evt.pixel);
      if (clusterFeatures.length > 0) {
        const feat = clusterFeatures[0].get('features');
        if (feat.length > 1) {
          mapElement.style.cursor = 'pointer';
          cursorChanged = true; // Cambiar el cursor una vez es suficiente
        }
      } else {
      }
    }

    // Si no se ha cambiado el cursor, verificar las features individuales
    if (!cursorChanged) {
      let featuresExist = olmap?.forEachFeatureAtPixel(evt.pixel, feature0 => {
        return true; // Con que encontremos una feature es suficiente
      });

      mapElement.style.cursor = featuresExist ? 'pointer' : 'default';
    }
  };
  const throttledAdjustZoomIndoorLayer = _.throttle(adjustZoomIndoorLayer, 1000);
  const throttledDisplayTooltip = _.throttle(displayTooltip, 0);
  const throttledCursorStyling = _.throttle(cursorStyling, 500);
  //#endregion

  //#region Function Styling
  //#region OpenLayers Styles
  const { textFill, textStroke, innerCircle, outerCircle, clusterText, backgroundMarker } = React.useMemo(() => {
    const primary = theme.palette.primary.main;

    const textFill = new OlFill({
      color: '#fff',
    });

    const textStroke = new OlStroke({
      color: 'rgba(0, 0, 0, 0.6)',
      width: 3,
    });

    const innerCircleFill = new OlFill({
      color: primary,
    });

    const outerCircleFill = new OlFill({
      color: alpha(primary, 0.5),
    });

    const innerCircle = (size: number) =>
      new OlCircleStyle({
        radius: Math.min(20, 10 + Math.log2(size) * 5),
        fill: innerCircleFill,
      });

    const outerCircle = (size: number) =>
      new OlCircleStyle({
        radius: Math.min(20, 10 + Math.log2(size) * 5) + (Math.log2(size) + 5),
        fill: outerCircleFill,
      });

    const clusterText = (size: number) =>
      new OlText({
        font: `${Math.min(18, 12 + Math.log2(size)) + 2}px Roboto`,
        text: size.toString(),
        fill: textFill,
        stroke: textStroke,
      });

    const backgroundMarker = () =>
      new OlStyle({
        zIndex: 5,
        image: new OlRegularShape({
          points: 4,
          radius: 16,
          angle: Math.PI / 4,
          fill: new OlFill({
            color: 'rgba(255, 0, 0, 0)',
          }),
        }),
      });

    return {
      textFill,
      textStroke,
      innerCircle,
      outerCircle,
      clusterText,
      backgroundMarker,
    };
  }, [theme.palette.primary.main]);
  //#endregion
  /**
   * This function updates the position of an icon and its icon style.
   * It's called when the feature geometry changes or when the feature is selected.
   * @param {CMapElement} item
   */
  const updateIconPositionAndIcon = (item: CMapElement) => {
    const itemID = item.getID();

    const geometry = item.mainFeature?.getGeometry();

    if (geometry) {
      const icon = item.getIcon(item.featureSelected);
      const color = item.getIconColor(item.featureSelected);
      const position = geometry.getCoordinates();
      generateIcon(itemID, position, icon, color);
    }
  };

  /**
   * Generates an icon for the given itemID and position, and associates it with the corresponding feature.
   * If the overlay already exists, it updates the position and icon of the existing overlay.
   * If the overlay does not exist, it creates a new overlay and adds it to the map.
   * @param {string} itemID The ID of the feature to which the icon will be associated.
   * @param {Coordinate} position The position of the icon.
   * @param {IconName} icon The name of the icon.
   */
  const generateIcon = (itemID: string, position: Coordinate, icon: string, color: string) => {
    const overlayIconExist = olmap?.getOverlayById(itemID);
    if (overlayIconExist) {
      overlayIconExist.setPosition(position);
      const oldElement = overlayIconExist.getElement();
      if (oldElement) {
        oldElement.innerHTML = ReactDOMServer.renderToStaticMarkup(<Icon isMap icon={icon} color={color} />);
      }
    } else {
      const iconElement = document.createElement('div');
      iconElement.id = itemID;
      iconElement.classList.add(idClusterLayer);
      iconElement.innerHTML = ReactDOMServer.renderToStaticMarkup(<Icon isMap icon={icon} color={color} />);
      iconElement.style.display = 'none';
      const overlay = new OlOverlay({
        id: itemID,
        element: iconElement,
        position: position,
        positioning: 'center-center',
        stopEvent: false,
      });
      olmap?.addOverlay(overlay);
    }
  };
  /**
   * Removes the icon associated with the given itemID from the map.
   * If no icon with the given itemID exists, this function does nothing.
   * @param {string} itemID The ID of the icon to be removed.
   */
  const deleteIcon = (itemID: string) => {
    const overlayToRemove = olmap?.getOverlayById(itemID);
    if (overlayToRemove) {
      olmap?.removeOverlay(overlayToRemove);
    }
  };
  //#endregion

  //#region Update Settings backend
  const debouncedOnChangeTransparencySlide = _.debounce(() => {
    putSettingsUserTileDefaultView();
  }, 1000);
  const putSettingsUserTileDefaultView = async () => {
    const { parameterId, defaultViewId } = settings?.defaultView || {};

    const handleUpdateSettings = v => {
      //dataStore._setUpdateSettings();
      const badge = getBadge();
      getSettings(dispatch, badge);
      dispatch(requestActions.cleanKey('defaultView'));
    };

    const tile = state.isSatelliteTile ? 'satellite' : 'normal';
    if (parameterId) {
      putUserDefaultView(dispatch, parameterId, defaultViewId, tile, state.transparency).then(handleUpdateSettings);
    } else {
      const badge = getBadge();
      postUserDefaultView(dispatch, badge.userId, badge.organizationId, defaultViewId, tile, state.transparency).then(
        handleUpdateSettings
      );
    }
    if (mapIndoorLayer) {
      mapIndoorLayer.transparency = state?.transparency ?? 0.5;
    }
  };
  //#endregion

  //#region Render
  const renderConfigMenu = () => {
    return (
      <div className={'slider ' + (state.isOpenConfig ? 'slider-show' : '')}>
        {state.isMapOutdoor && state.canEditMapIndoor && state.checkPermision && (
          <Button
            sx={{ all: 'unset', display: 'inline-block', textTransform: 'none' }}
            className="btn-edit-indoor-map"
            onClick={onClickEditMapIndoorLayer}
            label={[!state.isEditingMapIndoor ? 'Edit' : 'Save', ' ', 'background image']}
          />
        )}
        <Typography gutterBottom text="Transparency" />
        <Slider
          defaultValue={0.5}
          step={0.1}
          marks
          min={0}
          max={1}
          valueLabelDisplay="auto"
          value={state?.transparency}
          onChangeCommitted={(e, v) => {
            setState({ ...state, transparency: Number(v) });
            debouncedOnChangeTransparencySlide();
          }}
          onChange={(e, v) => {
            setState({ ...state, transparency: Number(v) });
          }}
          sx={{ width: '90%' }}
        />
      </div>
    );
  };

  const TTooltipMapMemoized = React.memo(TTooltipMap, (prevProps, nextProps) => {
    //We need to check the list of features id to compare
    const previdFeatures = prevProps.features.map(feature => feature?.getId()).filter(id => id !== undefined) as TElementID[];
    const nextidFeatures = nextProps.features.map(feature => feature?.getId()).filter(id => id !== undefined) as TElementID[];
    return JSON.stringify(previdFeatures) === JSON.stringify(nextidFeatures);
  });
  const renderTooltip = useCallback(
    _.throttle((listFeatures: FeatureLike[]) => {
      const featuresUnics = filterUniqueFeatures(listFeatures);
      /* const itemsMapUnics: Record<TElementID, CMapElement> = featuresUnics.reduce((acc, feature) => {
        const itemID = feature?.get('itemID');
        if (itemID) acc[itemID] = items[itemID];
        return acc;
      }, {} as Record<TElementID, CMapElement>); */

      return (
        <Provider store={store}>
          <TTooltipMapMemoized itemsMap={items} locations={locations} features={featuresUnics} />
        </Provider>
      );
    }, 1000),
    [items]
  );
  //#endregion

  const useEffect01InitOlMap = (deps: React.DependencyList) => {
    useEffect(() => {
      log.info(2, 'Mounting olmap');

      const attribution = new Attribution({
        collapsible: false,
      });

      const mousePositionControl = new OlMousePosition({
        coordinateFormat: coordinate => {
          return formatValue({ x: coordinate?.[0], y: coordinate?.[1] }, null, view.getReadingTypePosition());
        },
        projection: projectionCode,
      });

      const olMap = new OlMap({
        layers: isGPSMap ? [state.isSatelliteTile ? TILE_MAPS_LAYERS.satelliteLayer : TILE_MAPS_LAYERS.osmLayer] : [],
        view: mapView,
        controls: defaultControls({ attribution: false }).extend([attribution, mousePositionControl]),
      });
      setOlMap(olMap);
      setCycleState(prevState => ({ ...prevState, mountedMap: true }));

      return () => {
        log.info('Dismount');
        mapIndoorLayer && mapIndoorLayer.removeAllInteractionAndLayer();
        olmap?.setTarget();
        setOlMap(null);
        //Clean REFs
        mainFeaturesRef.current = new OlSourceVector({
          features: new OlCollection<any>(),
        });
        layersGeometriesRef.current = {};
        //featuresByItemRef.current = {};
        globalClusterLayerRef.current = null;

        clusterSourceRef.current = null;

        setCycleState(prevState => ({ ...prevState, mountedMap: false }));
      };
    }, deps);
  };

  const useEffect02AddEventsOlMapAndAddTooltipAndBackgroundLayerAndClusterLayer = (deps: React.DependencyList) => {
    useEffectAfterMount(() => {
      if (olmap && Object.keys(layers).length > 0) {
        log.info(4, 'Mounting ollayers in map and add events/tooltip');
        initializeMapLayers(layers);

        createGlobalClusterLayer();
        olmap.setTarget('map');

        olmap.once('postrender', afterRenderMapPrintBackground);
        setCycleState(prevState => ({ ...prevState, mountedCompleteMap: true }));

        generateOverlayTooltip();
      }
      return () => {
        log.info('Effect - Clean Add Events OlMap And Add Tooltip Layer And Background Layer');
        olmap?.un('postrender', afterRenderMapPrintBackground);
        olmap?.un('pointermove', throttledAdjustZoomIndoorLayer);
        setMapIndoorLayer(null);
      };
    }, deps);
  };

  const useEffect03AddGeometriesLayer = (deps: React.DependencyList) => {
    useEffectAfterMount(() => {
      if (cycleState.mountedMap) {
        if (locations && Object.keys(locations).length) {
          log.info(6, 'Effect - Add Geometries Layer And Create Global Cluster Layer');

          layers
            .filter(l => l.geometriesIds.length)
            .forEach(taLayer => {
              generateMapLayerGeometries(taLayer);
            });
          onRecenterMap(true);
        }
        olmap?.on('pointermove', throttledCursorStyling);
        mapIndoorLayer?.rePaintImageLayer();
      }
      return () => {
        olmap?.un('pointermove', throttledCursorStyling);
      };
    }, deps);
  };

  const useEffect04AddFeaturesOnMap = (deps: React.DependencyList) => {
    useEffectAfterMount(() => {
      if (cycleState.mountedCompleteMap && Object.keys(items).length) {
        //Generate colors and observer
        mapObserver.subscribe(callbackObserver);
        Object.keys(items).forEach(itemID => {
          const item = items[itemID];
          if (!item.observer) {
            item.observer = mapObserver;
            item.colorInMap = orderedColor.getColor();
          }
        });
        olmap?.on('pointermove', throttledDisplayTooltip);
      }
      return () => {
        olmap?.un('pointermove', throttledDisplayTooltip);
        mapObserver.unsubscribe(callbackObserver);
      };
    }, deps);
  };

  const useEffect05MQTTSuscriptionElement = (deps: React.DependencyList) => {
    useEffect(() => {
      if (cycleState.mountedCompleteMap && view) {
        log.info('Subscribing to items nº', Object.keys(items).length);
        const subscriptionAllItems = () => {
          if (view.properties.now) {
            Object.values(items).forEach(item => {
              if (!item.subscribed) item.connectMQTT();
            });
          } else {
            Object.values(items).forEach(item => {
              if (item.subscribed) item.disconnectMQTT();
            });
          }
        };
        subscriptionAllItems();
      }
    }, deps);
  };

  useEffect01InitOlMap([]);
  useEffect02AddEventsOlMapAndAddTooltipAndBackgroundLayerAndClusterLayer([cycleState.fetchedMapNeeds]);
  useEffect03AddGeometriesLayer([cycleState.mountedCompleteMap]);
  useEffect04AddFeaturesOnMap([cycleState.mountedCompleteMap, elementsMapSelected]); //elementsMapSelected
  useEffect05MQTTSuscriptionElement([cycleState.allTimelinePositionsFetched, view?.properties.now]);

  useEffectAfterMount(() => {
    if (olmap && mapIndoorLayer) {
      olmap.on('movestart', moveStartOnMap);
      olmap.on('moveend', moveEndOnMap);
    }
    return () => {
      olmap?.un('movestart', moveStartOnMap);
      olmap?.un('moveend', moveEndOnMap);
    };
  }, [mapIndoorLayer]);

  //Update cluster by filterState
  useEffectAfterMount(() => {
    filtersStateRef.current = filtersState;
    if (cycleState.allTimelinePositionsFetched) {
      log.info('ChangeFiltersState', filtersState);
      updateClusterFeatures(filtersState);
      if (globalClusterLayer?.getVisible()) {
        if (cycleState.mountedMap && filtersState.layers.list.length > 0) {
          filtersLayers(filtersState);
        }
      } else {
        elementsMapSelected.forEach(item => {
          setVisibilityIconDebounced(item.getID(), null, true);
        });
        if (elementSelected instanceof CLocation && elementSelected.itemsInsideByType) {
          globalClusterLayer?.setVisible(true);
        }
      }
    }
  }, [filtersState.layers.selected, filtersState.labels.selected, filtersState.items.selected]);

  useEffectAfterMount(() => {
    log.info('Map selection changed', { elementsMapSelected, elementSelected });

    // Always hide the cluster layer icons withou clustering
    if (elementsMapSelected.length > 0 && (globalClusterLayer?.isVisible() || itemsSelectedLayer?.isVisible()))
      setVisibilityIconDebounced(null, idClusterLayer, false);

    const itemsSource = itemsSelectedLayer?.getSource();

    // Helper: show all selected elements in the map
    const showAllSelectedElements = () => {
      if (!itemsSource) return;

      let needReCenterView = true;

      itemsSource.clear();

      elementsMapSelected.forEach(item => {
        if (!item) return;

        if (item.mainFeature) {
          setVisibilityIconDebounced(item.getID(), null, true);
          itemsSource.addFeature(item.mainFeature);

          if (item.worm) {
            itemsSource.addFeatures([...item.worm.markers, ...item.worm.lines]);
          }
        } else {
          // Caso "newItem" que tenías en el primer useEffect
          itemsSelectedLayer?.set('newItem', item.getID());
          needReCenterView = false;
        }
      });

      if (needReCenterView) {
        setTimeout(() => centerViewOnLayer(idItemSelectedLayer), 500);
      }
    };

    // 1) No selected elements in the map → global view
    if (elementsMapSelected.length === 0) {
      log.info('No elementsMapSelected → global cluster view');
      itemsSelectedLayer?.setVisible(false);
      globalClusterLayer?.setVisible(true);
      itemsSource?.clear();
      return;
    }

    // ItemsSelectedLayer visible
    globalClusterLayer?.setVisible(false);
    itemsSelectedLayer?.setVisible(true);

    // 2) If elementSelected is CLocation, no filters:
    if (elementSelected instanceof CLocation) {
      log.info('ElementSelected is CLocation → show all selected elements');
      showAllSelectedElements();
      return;
    }

    // 3) Only one item selected in the map:
    if (elementSelected instanceof CMapElement && elementSelected.mainFeature) {
      log.info('ElementSelected (CMapElement) → hide others', elementSelected);

      if (!itemsSource) return;

      itemsSource.clear();
      itemsSource.addFeature(elementSelected.mainFeature);
      setVisibilityIconDebounced(elementSelected.getID(), null, true);
      if (elementSelected.worm) {
        itemsSource.addFeatures([...elementSelected.worm.markers, ...elementSelected.worm.lines]);
      }

      // Hide others icons
      elementsMapSelected
        .filter(item => item.getID() !== elementSelected.getID())
        .forEach(item => {
          setVisibilityIconDebounced(item.getID(), null, false);
        });

      return;
    }

    // 4)Other case (elementSelected null/undefined/otro tipo):
    log.info('ElementSelected not a CMapElement with mainFeature → show all selected');
    showAllSelectedElements();
  }, [elementsMapSelected, elementSelected]);

  useEffectAfterMount(() => {
    let intervalId;
    let worker;
    if (cycleState.allTimelinePositionsFetched) {
      worker = new MapWorker();

      worker.onmessage = event => {
        if (event.data.error) {
          log.error('Worker error:', event.data.error);
        } else {
          const result = event.data;

          result.forEach(({ itemID, readingsToRemove }) => {
            const item = items[itemID];
            if (item) {
              item.removeReadings(readingTypeId, readingsToRemove);
            }
          });
        }
      };

      intervalId = setInterval(() => {
        if (!view.properties) return;
        const threshold = calculateThreshold(view.properties);
        worker.postMessage({
          items: Object.values(items)
            .filter(item => item.readings?.[readingTypeId]?.length > 0)
            .map(item => ({
              id: item.getID(),
              readings: [...item.readings[readingTypeId]],
            })),
          threshold,
        });

        setCycleState(prevState => ({ ...prevState, reSyncData: prevState.reSyncData + 1 }));
      }, 10000);
    }

    return () => {
      clearInterval(intervalId);

      worker?.terminate();
    };
  }, [cycleState.allTimelinePositionsFetched]);

  //#region UseEffect Secondaries
  //Update  userSettings when change Satellite Tile
  useEffectAfterMount(() => {
    if (mapIndoorLayer) {
      mapIndoorLayer.isSatelliteMap = state.isSatelliteTile;
    }
    putSettingsUserTileDefaultView();
  }, [state.isSatelliteTile]);

  //To control only when finish de loading
  useEffectDebounced(() => {
    if (loading) setMapLoading(true);
    if (!loading) setMapLoading(false);
    clusterSource?.refresh();
  }, [loading]);

  useEffectAfterMount(() => {
    olmap?.on('click', onClickMap);

    return () => {
      olmap?.un('click', onClickMap);
    };
  }, [cycleState.mountedCompleteMap]);
  //#endregion

  useImperativeHandle(ref, () => ({
    onCenterView() {
      onRecenterMap();
    },

    getOlmap() {
      return olmap;
    },

    cleanFeaturesMap() {
      log.info('CleanFeaturesMap');
      //mainFeaturesRef.current?.clear();
      //if (!globalClusterLayer?.getVisible()) {
      //  clusterSource?.getSource()?.clear();
      //} else {
      //  itemsSelectedLayer?.getSource()?.clear();
      //}
    },

    onChangeGlobalClusterVisibility(visibility: boolean) {
      globalClusterLayer?.setVisible(visibility);
    },
  }));
  const isLoading = mapLoading && Object.keys(items).length > 0;

  return (
    <div className="container-map">
      <div id="map" ref={mapRef} className="w-full bg-white" style={{ height: 'calc(100% + 15px)' }} />
      <div id="tooltip" />
      <div className="container-toolbar">
        <div className="container-left-btn-map">
          <button id="center" className="map-icon-btn" onClick={() => onRecenterMap()}>
            <Icon icon="my_location" className="map-icon" />
          </button>
          {isGPSMap && (
            <button id="satellite" className="map-icon-btn" onClick={onChangeTileView}>
              <Icon icon="maps-layers" className="map-icon" />
            </button>
          )}

          <div className="live-toolbar">
            <Tooltip
              title={
                isConnected
                  ? ['Live', ' (', 'Receiving last position', ')']
                  : ['Reconnecting', '…', 'If the problem persists, please refresh the page', '.']
              }
            >
              <Chip
                label="Live"
                icon={isConnected ? 'sensors' : 'sensors_off'}
                severity={isConnected ? 'success' : undefined}
                animate={isConnected}
              />
            </Tooltip>
          </div>

          {isLoading && (
            <div className="loading-toolbar">
              <Loading loading={isLoading} size={26} inline />
            </div>
          )}
        </div>
        <div className="container-right-btn-map">
          <Tooltip title="Settings" placement="top">
            <button id="settings" className="map-icon-btn" onClick={onClickEditConfiguration}>
              <Icon icon="settings" size={20} className="map-icon" />
            </button>
          </Tooltip>
          {renderConfigMenu()}
        </div>
      </div>

      <Modal
        withHeader
        open={state.isOpenDialogEditMapIndoor}
        title="Are you sure you want to edit the background image references?"
        warningToShow="This cannot be undone."
        onSubmit={onSubmitSaveMapIndoor}
        labelCancel="Cancel"
        labelOk="Ok"
        onClose={() => setState({ isOpenDialogEditMapIndoor: false })}
      />
    </div>
  );
});

export default TOlMap;
